import { useMemo } from "react";
import qrcode from "qrcode-generator";
import { cn } from "../lib/cn";

/**
 * Квадрат, который снимают камерой другого телефона.
 *
 * Рисуется как SVG, а не картинкой: он остаётся резким на любом экране и в
 * любом масштабе, а весь код — один `<path>`, который браузер отрисовывает
 * мгновенно.
 *
 * **Подложка всегда светлая, модули — тёмные, в обеих темах.** Это не небрежность
 * к тёмной теме, а то, как работают сканеры: инвертированный код (светлые модули
 * на тёмном фоне) часть камер просто не берёт, и человек стоит и водит телефоном
 * над экраном, не понимая, почему ничего не происходит. Поэтому цвета здесь
 * заданы значениями, а не токенами темы.
 *
 * Коррекция «M» — середина: запаса хватает на блик и на палец у края, а плотность
 * модулей остаётся такой, что дешёвая фронтальная камера ещё различает сетку.
 */

/** Тихая зона: без неё сканеру не за что зацепиться, четыре модуля — стандарт. */
const QUIET = 4;

const PLATE = "#fbf8f3";
const MODULE = "#1c1712";

export function QrCode({
  value,
  label,
  className,
}: {
  value: string;
  label: string;
  className?: string;
}) {
  const drawing = useMemo(() => {
    const qr = qrcode(0, "M");
    qr.addData(value);
    qr.make();

    const count = qr.getModuleCount();
    let path = "";

    for (let row = 0; row < count; row += 1) {
      for (let col = 0; col < count; col += 1) {
        // Один путь на весь код вместо тысячи <rect>: столько узлов в дереве
        // заметно тормозят даже на настольной машине.
        if (qr.isDark(row, col)) path += `M${col + QUIET} ${row + QUIET}h1v1h-1z`;
      }
    }

    return { size: count + QUIET * 2, path };
  }, [value]);

  return (
    <svg
      role="img"
      aria-label={label}
      viewBox={`0 0 ${drawing.size} ${drawing.size}`}
      // Модули ложатся ровно по сетке пикселей — сглаженные края у мелкого кода
      // размывают границу между модулями сильнее, чем кажется.
      shapeRendering="crispEdges"
      className={cn("h-auto w-full max-w-[18rem] rounded-card", className)}
      style={{ color: MODULE }}
    >
      <rect width={drawing.size} height={drawing.size} fill={PLATE} />
      <path d={drawing.path} fill="currentColor" />
    </svg>
  );
}
